import type { Action } from "redux";
import { put, race, select, take } from "redux-saga/effects";

import { MODAL_CONTEXT } from "constants/actionTypes";
import { AgriNousOfflineAction } from "actions/types";

export const chainActionFactory = nextAction =>
  function* fn() {
    yield put(nextAction());
  };

/**
 * Builds a saga that finds IDs referenced by one state slice but absent from
 * another, then dispatches a single action to fetch the missing records.
 */
export function fetchMissingByIdFactory<
  Source,
  Target,
  Id extends string | number,
  RequestAction extends Action,
>(
  getSourceById: (state: State) => Record<string, Source>,
  getTargetById: (state: State) => Record<Id, Target>,
  getTargetId: (source: Source) => Id,
  requestAction: (ids: Id[]) => RequestAction,
) {
  return function* fetchMissingById() {
    const state: State = yield select();
    const targetById = getTargetById(state);
    const missingIds = [
      ...new Set(
        Object.values(getSourceById(state))
          .map(getTargetId)
          .filter(id => !targetById[id]),
      ),
    ];

    if (missingIds.length > 0) {
      yield put(requestAction(missingIds));
    }
  };
}

export function modalUpdateActionPattern(modalType) {
  return action =>
    action.type === MODAL_CONTEXT.UPDATE.ACTION &&
    action.modalType === modalType;
}

export function* synchronousActionHandler(
  action,
  handler,
  successHandler,
  errorHandler,
) {
  const { options = {} } = action;
  const { onSuccess, onError } = options;
  try {
    const result = yield handler(action);
    try {
      typeof onSuccess === "function" && onSuccess(result);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "There was an error executing the synchronous success handler for %s",
        action.type,
        e,
      );
      try {
        typeof onError === "function" && onError(e);
      } catch (e1) {
        // eslint-disable-next-line no-console
        console.warn(
          "There was an error executing the synchronous error handler for %s",
          action.type,
          e1,
        );
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "There was an error executing the handler for %s",
      action.type,
      e,
    );
    if (typeof errorHandler === "function") {
      return yield errorHandler(action, e);
    }
    return;
  }
  if (typeof successHandler === "function") {
    return yield successHandler(action);
  }
}

export function* takeActionOutcome<SuccessAction, FailureAction>(
  successActionType: string,
  failureActionType: string,
  onSuccess: (action: SuccessAction) => void,
  onFailure: (action: FailureAction) => void,
) {
  const { success, failure } = yield race({
    success: take(successActionType),
    failure: take(failureActionType),
  });
  if (success) {
    onSuccess(success);
  } else if (failure) {
    onFailure(failure);
  }
}

type OfflineCommitAction<T extends AgriNousOfflineAction> =
  T["meta"]["offline"]["commit"];

type OfflineRollbackAction<T extends AgriNousOfflineAction> =
  T["meta"]["offline"]["rollback"];

export function* takeOfflineActionOutcome<
  TAction extends AgriNousOfflineAction,
>(
  action: TAction,
  onSuccess: (action: OfflineCommitAction<TAction>) => void,
  onFailure: (action: OfflineRollbackAction<TAction>) => void,
) {
  yield takeActionOutcome(
    action.meta.offline.commit.type,
    action.meta.offline.rollback.type,
    onSuccess,
    onFailure,
  );
}
